/**
 * /producao/minhas-etapas — atalho mobile do responsável de setor
 * (Lote B — §2.5 "interface mobile").
 *
 * Lista as etapas correntes (PENDENTE/EM_EXECUCAO por definição — a etapa
 * corrente do board é a primeira não concluída) dos ambientes ativos, em
 * cards touch grandes, com Iniciar/Concluir inline sob os MESMOS gates do
 * Detalhe: permissão por fase (producao_fase:operar_f1..f6), checkpoint
 * conclui só por bipagem, congelado bloqueia.
 *
 * Fonte: GET /api/producao/board (sem endpoint novo). Limitações honestas
 * do payload do board:
 *   - o board não expõe o RESPONSÁVEL da etapa — o filtro "só as minhas"
 *     usa as fases que o usuário PODE OPERAR; o responsável aparece ao
 *     expandir o card (busca o Detalhe sob demanda);
 *   - o board não expõe o id/estado da EtapaExecucao — o expand busca
 *     GET /api/ambientes-producao/{id} (1 request por card aberto).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiChevronUp, FiExternalLink } from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  ESTADO_ETAPA_LABELS,
  ESTADO_ETAPA_TONES,
  FASE_INFO,
  boardPorEtapa,
  concluirEtapa,
  erroEhCheckpointBipagem,
  iniciarEtapa,
  obterAmbiente,
  permissaoExtraConcluirEtapa,
  permissaoOperarFase,
} from "../../services/producaoV2";
import { formatarDataHoraCurta } from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  WarningMsg,
} from "../Orcamentos/styles";
import { LinhaErro, Mono, NotaDiscreta, Shimmer } from "./_shared/ui";
import { useCheckpointsBipagem } from "./_shared/useCheckpointsBipagem";

const ESTADO_PENDENTE = "PENDENTE";
const ESTADO_CONCLUIDA = "CONCLUIDA";
const ESTADO_NAO_APLICAVEL = "NAO_APLICAVEL";

const CHAVE_SO_MINHAS = "prod.minhasEtapas.soMinhas";
const PESO_ZONA = { vermelho: 0, amarelo: 1, verde: 2 };

const COR_ZONA = {
  verde: "var(--success-solid)",
  amarelo: "var(--warning-solid)",
  vermelho: "var(--danger-solid)",
};

function lerSoMinhasInicial() {
  try {
    return window.localStorage.getItem(CHAVE_SO_MINHAS) !== "false";
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Styled — mobile-first (cards touch, zero scroll lateral)
// ---------------------------------------------------------------------------

const Pagina = styled(PageContainer)`
  max-width: 760px;
`;

const BarraFiltro = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const GrupoToggle = styled.div`
  display: inline-flex;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-md, 8px);
  padding: 2px;
`;

const ChipToggleVista = styled.button`
  border: none;
  background: ${({ $ativo }) => ($ativo ? "var(--surface)" : "transparent")};
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-3)")};
  box-shadow: ${({ $ativo }) => ($ativo ? "var(--shadow-1)" : "none")};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  min-height: 40px;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ListaCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardEtapa = styled.article`
  background: var(--surface);
  border: 1px solid
    ${({ $congelado }) =>
      $congelado ? "var(--warning-border)" : "var(--border)"};
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  overflow: hidden;
`;

const CabecaCard = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  color: inherit;
  padding: 14px 16px;
  cursor: pointer;
  min-height: 44px;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  .linha1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .titulo {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .contexto {
    font-size: 12px;
    color: var(--ink-3);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pills {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 8px;
  }
`;

const DotBuffer = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $zona }) => COR_ZONA[$zona] || COR_ZONA.verde};
`;

const CorpoCard = styled.div`
  border-top: 1px solid var(--border-subtle);
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LinhaMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--ink-2);

  .rotulo {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ink-3);
  }
`;

const AcoesCard = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  button {
    flex: 1 1 140px;
    min-height: 44px;
    font-size: 0.9rem;
  }
`;

const LinkDetalhe = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  min-height: 44px;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

// ---------------------------------------------------------------------------

export default function MinhasEtapas() {
  const navigate = useNavigate();
  const toast = useToast();
  const { request, hasPermission } = useAuth();
  const checkpoints = useCheckpointsBipagem();

  const [board, setBoard] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [soMinhas, setSoMinhas] = useState(lerSoMinhasInicial);
  const [expandidoId, setExpandidoId] = useState(null);
  // {ambienteId: {carregando, erro, ambiente}} — Detalhe sob demanda.
  const [detalhes, setDetalhes] = useState({});
  const [erroAcao, setErroAcao] = useState(null); // {id, mensagem}

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_SO_MINHAS, String(soMinhas));
    } catch {
      /* storage indisponível */
    }
  }, [soMinhas]);

  const carregar = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!silencioso) {
        setErro(null);
        setCarregando(true);
      }
      try {
        setBoard(await boardPorEtapa(request));
      } catch (err) {
        if (!silencioso) {
          setErro(err?.message || "Não foi possível carregar as etapas.");
        }
      } finally {
        if (!silencioso) setCarregando(false);
      }
    },
    [request],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const podeOperarFaseDe = useCallback(
    (fase) => {
      const permissao = permissaoOperarFase(fase);
      return Boolean(permissao) && hasPermission(permissao);
    },
    [hasPermission],
  );

  const operaAlgumaFase = useMemo(
    () =>
      Object.keys(FASE_INFO).some(
        (fase) => permissaoOperarFase(fase) && podeOperarFaseDe(fase),
      ),
    [podeOperarFaseDe],
  );

  // Flatten do board: 1 entrada por (ambiente × etapa corrente). A etapa
  // corrente é PENDENTE/EM_EXECUCAO por definição (primeira não concluída).
  const entradas = useMemo(() => {
    const todas = [];
    for (const etapa of board?.etapas || []) {
      for (const ambiente of etapa.ambientes || []) {
        todas.push({ ambiente, etapa });
      }
    }
    const filtradas = soMinhas
      ? todas.filter((e) => podeOperarFaseDe(e.etapa.fase))
      : todas;
    return filtradas.sort((a, b) => {
      const za = PESO_ZONA[a.ambiente.buffer?.zona] ?? 2;
      const zb = PESO_ZONA[b.ambiente.buffer?.zona] ?? 2;
      if (za !== zb) return za - zb;
      return (b.ambiente.buffer?.pct ?? 0) - (a.ambiente.buffer?.pct ?? 0);
    });
  }, [board, soMinhas, podeOperarFaseDe]);

  const alternarExpandido = useCallback(
    (entrada) => {
      const id = entrada.ambiente.id;
      setErroAcao(null);
      if (expandidoId === id) {
        setExpandidoId(null);
        return;
      }
      setExpandidoId(id);
      // Busca o Detalhe sob demanda (o board não traz id/estado/responsável
      // da etapa) — cacheado até uma ação atualizar.
      if (!detalhes[id]?.ambiente && !detalhes[id]?.carregando) {
        setDetalhes((m) => ({ ...m, [id]: { carregando: true } }));
        obterAmbiente(request, id)
          .then((ambiente) =>
            setDetalhes((m) => ({ ...m, [id]: { ambiente } })),
          )
          .catch((err) =>
            setDetalhes((m) => ({
              ...m,
              [id]: {
                erro:
                  err?.message || "Não foi possível carregar a etapa.",
              },
            })),
          );
      }
    },
    [expandidoId, detalhes, request],
  );

  const executarAcao = useCallback(
    async (entrada, etapa, acao) => {
      const id = entrada.ambiente.id;
      setErroAcao(null);
      try {
        const fn = acao === "iniciar" ? iniciarEtapa : concluirEtapa;
        const resposta = await fn(request, id, etapa.id, {
          silentError: true,
        });
        setDetalhes((m) => ({ ...m, [id]: { ambiente: resposta } }));
        toast.success(
          `Etapa ${etapa.label} ${acao === "iniciar" ? "iniciada" : "concluída"}.`,
        );
        await carregar({ silencioso: true });
      } catch (err) {
        const mensagem = erroEhCheckpointBipagem(err)
          ? `«${etapa.label}» é um checkpoint de bipagem — a conclusão `
            + "acontece peça a peça no Terminal."
          : err?.payload?.detalhe
            || err?.message
            || `Não foi possível ${acao} a etapa.`;
        setErroAcao({ id, mensagem });
        throw err; // AsyncButton silentError engole (sem toast duplo)
      }
    },
    [request, toast, carregar],
  );

  const renderCorpo = (entrada) => {
    const id = entrada.ambiente.id;
    const info = detalhes[id] || {};
    if (info.carregando || (!info.ambiente && !info.erro)) {
      return <Shimmer $h={72} />;
    }
    if (info.erro) {
      return <ErrorMsg>{info.erro}</ErrorMsg>;
    }
    const ambiente = info.ambiente;
    const etapa = (ambiente.etapas || []).find(
      (e) =>
        e.codigo === entrada.etapa.codigo
        && e.estado !== ESTADO_NAO_APLICAVEL
        && e.estado !== ESTADO_CONCLUIDA,
    );
    if (!etapa) {
      return (
        <WarningMsg>
          A etapa atual deste ambiente mudou desde o carregamento — puxe a
          lista de novo.
        </WarningMsg>
      );
    }
    const ehCheckpoint = checkpoints.has(etapa.codigo);
    const podeOperar = podeOperarFaseDe(etapa.fase);
    const permExtraConcluir = permissaoExtraConcluirEtapa(etapa.codigo);
    const podeConcluir = !permExtraConcluir || hasPermission(permExtraConcluir);
    const bloqueado = ambiente.cancelado || ambiente.congelado;

    return (
      <>
        <LinhaMeta>
          <span className="rotulo">Estado</span>
          <Pill $tone={ESTADO_ETAPA_TONES[etapa.estado]}>
            {ESTADO_ETAPA_LABELS[etapa.estado] || etapa.estado}
          </Pill>
          <span className="rotulo">Responsável</span>
          <span>{etapa.responsavel_nome || "–"}</span>
        </LinhaMeta>
        {etapa.iniciada_em && (
          <LinhaMeta>
            <span className="rotulo">Início</span>
            <Mono>{formatarDataHoraCurta(etapa.iniciada_em)}</Mono>
          </LinhaMeta>
        )}

        {erroAcao?.id === id && <WarningMsg>{erroAcao.mensagem}</WarningMsg>}

        {ehCheckpoint ? (
          <Pill
            $tone="neutral"
            style={{ alignSelf: "flex-start" }}
            title="Etapa-checkpoint: inicia e conclui peça a peça pela bipagem no Terminal — sem conclusão manual"
          >
            conclui por bipagem
          </Pill>
        ) : !podeOperar ? (
          <NotaDiscreta style={{ margin: 0 }}>
            Você não tem permissão de operação nesta fase (
            {FASE_INFO[etapa.fase]?.rotulo || etapa.fase}).
          </NotaDiscreta>
        ) : (
          <AcoesCard>
            {etapa.estado === ESTADO_PENDENTE && (
              <AsyncButton
                variant="secondary"
                silentError
                silentSuccess
                disabled={bloqueado}
                title={
                  ambiente.congelado
                    ? "Ambiente congelado — descongele para operar"
                    : undefined
                }
                onClick={() => executarAcao(entrada, etapa, "iniciar")}
              >
                Iniciar
              </AsyncButton>
            )}
            {podeConcluir && (
              <AsyncButton
                silentError
                silentSuccess
                disabled={bloqueado}
                title={
                  ambiente.congelado
                    ? "Ambiente congelado — descongele para operar"
                    : undefined
                }
                onClick={() => executarAcao(entrada, etapa, "concluir")}
              >
                Concluir
              </AsyncButton>
            )}
          </AcoesCard>
        )}

        <LinkDetalhe
          type="button"
          onClick={() => navigate(`/producao/ambientes/${id}`)}
        >
          Abrir detalhe do ambiente
          <FiExternalLink size={13} aria-hidden="true" />
        </LinkDetalhe>
      </>
    );
  };

  return (
    <Pagina>
      <EntityHeader
        title="Minhas etapas"
        subtitle="Etapas pendentes e em execução das fases que você opera"
        badges={[
          { label: `${entradas.length} etapa(s)`, tone: "neutral" },
        ]}
      />

      <BarraFiltro>
        <GrupoToggle role="tablist" aria-label="Filtro de etapas">
          <ChipToggleVista
            type="button"
            role="tab"
            aria-selected={soMinhas}
            $ativo={soMinhas}
            onClick={() => setSoMinhas(true)}
          >
            Só as minhas
          </ChipToggleVista>
          <ChipToggleVista
            type="button"
            role="tab"
            aria-selected={!soMinhas}
            $ativo={!soMinhas}
            onClick={() => setSoMinhas(false)}
          >
            Todas
          </ChipToggleVista>
        </GrupoToggle>
        <SecondaryButton
          type="button"
          onClick={() => carregar()}
          style={{ minHeight: 40 }}
        >
          Atualizar
        </SecondaryButton>
      </BarraFiltro>

      {erro && (
        <LinhaErro style={{ marginBottom: 14 }}>
          <ErrorMsg style={{ flex: 1 }}>{erro}</ErrorMsg>
          <SecondaryButton type="button" onClick={() => carregar()}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {carregando ? (
        <ListaCards>
          <Shimmer $h={96} />
          <Shimmer $h={96} />
          <Shimmer $h={96} />
        </ListaCards>
      ) : entradas.length === 0 ? (
        <EmptyMsg>
          {soMinhas && !operaAlgumaFase
            ? "Você não tem permissão de operação em nenhuma fase — veja com o gestor de acessos ou toque em «Todas» para acompanhar."
            : soMinhas
              ? "Nenhuma etapa pendente ou em execução nas fases que você opera."
              : "Nenhum ambiente em produção no momento."}
        </EmptyMsg>
      ) : (
        <ListaCards>
          {entradas.map((entrada) => {
            const a = entrada.ambiente;
            const aberto = expandidoId === a.id;
            return (
              <CardEtapa key={`${a.id}-${entrada.etapa.codigo}`} $congelado={a.congelado}>
                <CabecaCard
                  type="button"
                  aria-expanded={aberto}
                  onClick={() => alternarExpandido(entrada)}
                >
                  <div className="linha1">
                    <span className="titulo">
                      <Mono>{a.numero}</Mono> · {a.nome}
                    </span>
                    {aberto ? (
                      <FiChevronUp size={16} aria-hidden="true" />
                    ) : (
                      <FiChevronDown size={16} aria-hidden="true" />
                    )}
                  </div>
                  <div className="contexto">
                    {a.pedido_numero}
                    {a.cliente_nome ? ` · ${a.cliente_nome}` : ""}
                  </div>
                  <div className="pills">
                    <Pill $tone="purple">
                      {FASE_INFO[entrada.etapa.fase]?.curto
                        || entrada.etapa.fase}
                    </Pill>
                    <Pill $tone="neutral">{entrada.etapa.label}</Pill>
                    {a.congelado && <Pill $tone="warning">Congelado</Pill>}
                    <span
                      title={`Consumo de buffer: ${a.buffer?.pct ?? 0}% (zona ${a.buffer?.zona || "verde"})`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: "var(--ink-3)",
                      }}
                    >
                      <DotBuffer
                        $zona={a.buffer?.zona}
                        aria-hidden="true"
                      />
                      <Mono>{a.buffer?.pct ?? 0}%</Mono>
                    </span>
                  </div>
                </CabecaCard>
                {aberto && <CorpoCard>{renderCorpo(entrada)}</CorpoCard>}
              </CardEtapa>
            );
          })}
        </ListaCards>
      )}

      <NotaDiscreta>
        Filtro «só as minhas» = fases que você pode operar
        (producao_fase:operar_f1..f6). O responsável da etapa aparece ao
        abrir o card — a listagem do board ainda não expõe esse vínculo.
      </NotaDiscreta>
    </Pagina>
  );
}
